import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import "./TwoMinuteReset.css";

export default function TwoMinuteReset() {
  // timer state (seconds)
  const TOTAL_SECONDS = 120;
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  // breathing cycle config (seconds)
  // gentle pattern: inhale 4s, hold 1s, exhale 5s  => 10s per cycle
  const BREATH = { inhale: 4, hold: 1, exhale: 5 };
  const cycleLength = BREATH.inhale + BREATH.hold + BREATH.exhale;

  // for animation: value 0..1 controlling scale, updated every 250ms when running
  const [animProgress, setAnimProgress] = useState(0);
  const animRef = useRef(null);

  // compute cue text from secondsLeft
  function getCueText(totalElapsedSeconds) {
    // totalElapsedSeconds counts from 0 at start to TOTAL_SECONDS
    // we want the phase based on elapsed within cycle
    const elapsedInCycle = (totalElapsedSeconds % cycleLength);
    if (elapsedInCycle < BREATH.inhale) return "Inhale";
    if (elapsedInCycle < BREATH.inhale + BREATH.hold) return "Hold";
    return "Exhale";
  }

  // format mm:ss
  function formatTime(s) {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  }

  // start / pause
  const startPause = () => {
    setRunning((r) => !r);
  };

  const reset = () => {
    setRunning(false);
    setSecondsLeft(TOTAL_SECONDS);
    setAnimProgress(0);
  };

  // main countdown interval (1s)
  useEffect(() => {
    if (running && intervalRef.current == null) {
      intervalRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            // stop at 0
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            setRunning(false);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }

    if (!running && intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [running]);

  // animation progression (updates fast for smooth scaling)
  useEffect(() => {
    if (running && animRef.current == null) {
      // update animProgress every 200ms
      animRef.current = setInterval(() => {
        setAnimProgress((p) => (p + 0.2) % cycleLength); // we store progress in seconds-like units
      }, 200);
    }
    if (!running && animRef.current != null) {
      clearInterval(animRef.current);
      animRef.current = null;
      setAnimProgress((p) => p % cycleLength);
    }
    return () => {
      if (animRef.current) {
        clearInterval(animRef.current);
        animRef.current = null;
      }
    };
  }, [running]);

  // derive cue and scale from elapsed time
  const elapsed = TOTAL_SECONDS - secondsLeft; // elapsed seconds since start
  const cue = running ? getCueText(elapsed) : secondsLeft === TOTAL_SECONDS ? "Ready" : getCueText(elapsed);
  // compute a scale value for circle from animProgress (in seconds-like)
  const animValue = animProgress; // 0..cycleLength
  let scale = 1;
  // mapping:
  // inhale (0..inhale): scale 0.85 -> 1.15 (grow)
  // hold: stay near 1.15
  // exhale: scale 1.15 -> 0.85 (shrink)
  if (animValue < BREATH.inhale) {
    const t = animValue / BREATH.inhale;
    scale = 0.85 + (1.15 - 0.85) * t;
  } else if (animValue < BREATH.inhale + BREATH.hold) {
    scale = 1.15;
  } else {
    const t = (animValue - (BREATH.inhale + BREATH.hold)) / BREATH.exhale;
    scale = 1.15 + (0.85 - 1.15) * t;
  }

  // ensure scale is numeric
  scale = Number(scale.toFixed(3));

  // accessible label for button
  const startLabel = running ? "Pause breathing" : "Start 2-minute reset";

  return (
    <div className="twomin-wrapper">
      <header className="twomin-header">
        <div className="brand">CalmNote</div>
        <p className="subtitle">The 2-Minute Reset — a quick breathing exercise to regain focus.</p>
      </header>

      <main className="twomin-main">
        <article className="post-card" aria-labelledby="post-title">
          <span className="kicker">Featured practice</span>
          <h1 id="post-title" className="title">The 2-Minute Reset</h1>
          <div className="meta">Estimated read: 7 minutes • Quick, portable breathing practice</div>

          <div className="layout">
            <section className="content">
              <p className="lead">
                Two minutes is long enough to shift your breath and calm your system, but short enough to actually do.
                Use this guide and the practice widget to reset, sharpen attention, and come back to the present moment.
              </p>

              <h2>Why it works (plain English)</h2>
              <p>
                Breathing influences the autonomic nervous system. Slow, intentional exhalations activate the vagus nerve and
                move the body toward a calmer state. Even brief practices repeated daily improve attention and resilience.
              </p>

              <h2>How to do the 2-minute reset</h2>
              <ol>
                <li><strong>Find a posture:</strong> sit or stand with an upright spine and relaxed shoulders.</li>
                <li><strong>Anchor for a breath:</strong> notice the natural inhalation and exhalation — no change yet.</li>
                <li><strong>Follow the rhythm:</strong> inhale for ~4s, hold ~1s, exhale ~5s. Repeat for two minutes.</li>
                <li><strong>Finish gently:</strong> return to your normal breath and notice how you feel for a few breaths.</li>
              </ol>

              <h2>When to use it</h2>
              <ul>
                <li>Before a meeting or call to set a calm tone.</li>
                <li>After an interruption to regain focus.</li>
                <li>In a long queue, waiting room, or standing in line.</li>
              </ul>

              <blockquote>
                Tip: consistency wins. Doing two minutes daily is more effective than long sessions you never complete.
              </blockquote>

              <h2>Variations</h2>
              <div className="variation-grid">
                <div className="variation">
                  <h3>Faster reset (1 minute)</h3>
                  <p>Shorten each phase proportionally (inhale 3s, hold 1s, exhale 3s) and run for 60 seconds.</p>
                </div>
                <div className="variation">
                  <h3>Longer calm (4 minutes)</h3>
                  <p>Keep the same rhythm but go for 4 minutes if you have time and want deeper relaxation.</p>
                </div>
              </div>

              <h2>FAQ</h2>
              <p><strong>Q:</strong> What if I can't breathe like the counts say? <br />
              <strong>A:</strong> Use smaller counts that feel comfortable — the precise numbers matter less than the steady pattern.</p>

              <p className="muted small-note">If you have respiratory or cardiac conditions, check with a clinician before trying paced breathing exercises.</p>
            </section>

            <aside className="sidebar" aria-labelledby="practice-label">
              <div className="box" role="region" aria-labelledby="practice-label">
                <h4 id="practice-label">Practice — 2 minutes</h4>

                <div className="widget" aria-hidden="false">
                  <div
                    className="bubble"
                    style={{ transform: `scale(${scale})` }}
                    aria-hidden="true"
                  />
                  <div className="cue" aria-live="polite">{cue}</div>
                  <div className="countdown" aria-live="polite">{formatTime(secondsLeft)}</div>

                  <div className="controls">
                    <button
                      className="btn start"
                      onClick={startPause}
                      aria-pressed={running}
                      aria-label={startLabel}
                    >
                      {running ? "Pause" : "Start"}
                    </button>
                    <button
                      className="btn reset"
                      onClick={reset}
                      aria-label="Reset timer"
                    >
                      Reset
                    </button>
                  </div>

                  <div className="hints">
                    <span className="pill">No signup</span>
                    <span className="pill">2 min</span>
                    <span className="pill">Gentle</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <footer className="post-footer">
              <Link to={'/privacyPolicy'}>
                    Pivacy Policy
                </Link>
            <p className="muted">© 2025 CalmNote — small practices, steady results.</p>
            
          </footer>
        </article>
      </main>
    </div>
  );
}


